import React, { useState, useEffect, useRef } from "react";
import { FiX, FiSend, FiChevronDown, FiChevronUp, FiFileText, FiCheck, FiCheckCircle } from "react-icons/fi";
import api from "../../api";
import "./EmailReplyModal.css";

const EmailReplyModal = ({ email, onClose, onSend, showToast, replyAll = false, person = null, currentUser = null }) => {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [variableValues, setVariableValues] = useState({});
  const bodyEditorRef = useRef(null);

  useEffect(() => {
    // Fetch email templates
    const fetchTemplates = async () => {
      try {
        const response = await api.get("/email-templates");
        setTemplates(response.data || []);
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (email) {
      // Extract recipient email
      const headers = email.payload?.headers || [];
      const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || "";
      
      const fromHeader = getHeader("From");
      const toHeader = getHeader("To");
      const ccHeader = getHeader("Cc");
      
      // Extract email addresses from "Name <email@domain.com>" format
      const extractEmails = (headerText) => {
        if (!headerText) return [];
        
        // Smart split that respects quoted strings and angle brackets
        const emails = [];
        let current = '';
        let inQuotes = false;
        let inAngles = false;
        
        for (let i = 0; i < headerText.length; i++) {
          const char = headerText[i];
          
          if (char === '"' && !inAngles) {
            inQuotes = !inQuotes;
            current += char;
          } else if (char === '<' && !inQuotes) {
            inAngles = true;
            current += char;
          } else if (char === '>' && !inQuotes) {
            inAngles = false;
            current += char;
          } else if (char === ',' && !inQuotes && !inAngles) {
            // This is a real separator
            if (current.trim()) {
              emails.push(current.trim());
            }
            current = '';
          } else {
            current += char;
          }
        }
        
        // Don't forget the last email
        if (current.trim()) {
          emails.push(current.trim());
        }
        
        // Extract just the email address from each entry
        return emails.map(addr => {
          const match = addr.match(/<([^>]+)>/);
          return match ? match[1].trim() : addr.replace(/["\s]/g, '').trim();
        });
      };

      // Determine recipient
      const isSent = email.labelIds?.includes("SENT");
      
      if (replyAll) {
        // Reply All: include original recipients
        if (isSent) {
          // If we sent it, reply to all original recipients
          setTo(extractEmails(toHeader).join(", "));
          setCc(ccHeader ? extractEmails(ccHeader).join(", ") : "");
        } else {
          // If we received it, reply to sender and include all other recipients in CC
          const allToAddresses = extractEmails(toHeader);
          const allCcAddresses = ccHeader ? extractEmails(ccHeader) : [];
          const senderEmail = extractEmails(fromHeader)[0];
          
          setTo(senderEmail);
          
          // Combine To and CC, excluding our own address
          const ccList = [...allToAddresses, ...allCcAddresses].filter(addr => 
            addr.toLowerCase() !== senderEmail.toLowerCase()
          );
          setCc(ccList.join(", "));
        }
        setShowCcBcc(true); // Auto-show CC/BCC for Reply All
      } else {
        // Regular Reply: only to sender
        const recipientEmail = isSent ? extractEmails(toHeader)[0] : extractEmails(fromHeader)[0];
        setTo(recipientEmail);
        setCc("");
        setBcc("");
      }
      
      // Set subject with "Re: " prefix if not already present
      const originalSubject = getHeader("Subject") || "(No Subject)";
      const replySubject = originalSubject.startsWith("Re:") ? originalSubject : `Re: ${originalSubject}`;
      setSubject(replySubject);
      
      // Extract full body text for quoting
      let bodyText = "";
      if (email.payload?.parts) {
        const textPart = email.payload.parts.find(part => part.mimeType === "text/plain");
        if (textPart && textPart.body && textPart.body.data) {
          bodyText = atob(textPart.body.data.replace(/-/g, "+").replace(/_/g, "/"));
        } else {
          const htmlPart = email.payload.parts.find(part => part.mimeType === "text/html");
          if (htmlPart && htmlPart.body && htmlPart.body.data) {
            const html = atob(htmlPart.body.data.replace(/-/g, "+").replace(/_/g, "/"));
            // Strip HTML tags for plain text
            bodyText = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
          }
        }
      } else if (email.payload?.body && email.payload.body.data) {
        bodyText = atob(email.payload.body.data.replace(/-/g, "+").replace(/_/g, "/"));
      }
      
      // Use full body if available, otherwise fall back to snippet
      const quotedText = bodyText || email.snippet || "";
      setBody(`\n\n--- Original Message ---\nFrom: ${fromHeader}\nDate: ${new Date(Number(email.internalDate)).toLocaleString()}\nSubject: ${originalSubject}\n\n${quotedText}\n`);
    }
  }, [email, replyAll]);

  // Function to get default value for a variable from person/user data
  const getDefaultVariableValue = (variableName) => {
    const varLower = variableName.toLowerCase();
    
    // Person-related variables
    if (person) {
      if (varLower === 'name') return person.name || '';
      if (varLower === 'firstname') return person.name?.split(' ')[0] || '';
      if (varLower === 'lastname') return person.name?.split(' ').slice(1).join(' ') || '';
      if (varLower === 'email') return person.email || '';
      if (varLower === 'company') return person.company || '';
      if (varLower === 'phone') return person.phone || '';
      if (varLower === 'status') return person.status || '';
    }
    
    // Current user variables
    if (currentUser) {
      if (varLower === 'myname') return currentUser.name || '';
      if (varLower === 'myemail') return currentUser.email || '';
      if (varLower === 'mycompany') return currentUser.company || '';
    }
    
    // Email-related variables
    if (email) {
      const headers = email.payload?.headers || [];
      const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || "";
      
      if (varLower === 'subject') return getHeader("Subject") || "";
    }
    
    return '';
  };

  // Function to detect variables in text
  const detectVariables = (text) => {
    if (!text) return [];
    
    const variableRegex = /\{\{(\w+)\}\}/gi;
    const matches = [...text.matchAll(variableRegex)];
    const uniqueVars = [...new Set(matches.map(match => match[1].toLowerCase()))];
    
    return uniqueVars;
  };

  // Compute detected variables from subject and body
  const detectedVars = [...new Set([
    ...detectVariables(subject),
    ...detectVariables(body)
  ])];

  // Initialize variable values when detected variables change
  useEffect(() => {
    const newVars = {};
    detectedVars.forEach(varName => {
      // Only set if not already set (preserve user edits)
      if (!(varName in variableValues)) {
        newVars[varName] = getDefaultVariableValue(varName);
      }
    });
    
    if (Object.keys(newVars).length > 0) {
      setVariableValues(prev => ({ ...prev, ...newVars }));
    }
  }, [detectedVars.join(',')]);

  // Function to render text with variable chips as React elements
  const renderTextWithChips = (text) => {
    if (!text) return null;
    
    const variableRegex = /\{\{(\w+)\}\}/gi;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    
    while ((match = variableRegex.exec(text)) !== null) {
      // Add text before the variable
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${key++}`}>{text.substring(lastIndex, match.index)}</span>
        );
      }
      
      // Add the variable chip
      const varName = match[1].toLowerCase();
      const value = variableValues[varName] || '';
      parts.push(
        <span 
          key={`chip-${key++}`} 
          className={`editor-chip ${value ? 'has-value' : 'no-value'}`}
          contentEditable={false}
          data-variable={varName}
        >
          <span className="chip-var">{match[1]}</span>
          <span className="chip-separator">:</span>
          <span className="chip-value">{value || '?'}</span>
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key={`text-${key++}`}>{text.substring(lastIndex)}</span>
      );
    }
    
    return parts;
  };

  // Handle contentEditable body changes
  const handleBodyEditorInput = (e) => {
    const element = e.currentTarget;
    
    // Parse the HTML content and convert chips back to {{variable}} syntax
    const parseContent = (node) => {
      let text = '';
      
      node.childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
          text += child.textContent;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.classList && child.classList.contains('editor-chip')) {
            // This is a variable chip - convert back to {{variable}}
            const varName = child.getAttribute('data-variable');
            text += `{{${varName}}}`;
          } else if (child.tagName === 'BR') {
            text += '\n';
          } else {
            // Recursively parse other elements
            text += parseContent(child);
          }
        }
      });
      
      return text;
    };
    
    const parsedText = parseContent(element);
    setBody(parsedText);
  };

  // Initialize the contentEditable div when email/body changes (modal opens)
  useEffect(() => {
    if (bodyEditorRef.current && body) {
      const rendered = renderBodyWithChips(body);
      bodyEditorRef.current.innerHTML = rendered;
    }
  }, [email]); // Initialize when email changes (modal opens)

  // Update only when variables change (not when typing)
  useEffect(() => {
    if (bodyEditorRef.current) {
      const rendered = renderBodyWithChips(body);
      // Only update if not actively typing (prevents cursor jump)
      if (document.activeElement !== bodyEditorRef.current) {
        if (bodyEditorRef.current.innerHTML !== rendered) {
          bodyEditorRef.current.innerHTML = rendered;
        }
      }
    }
  }, [variableValues]); // Only re-render when variables change, not when body changes

  // Render body with chips as HTML string
  const renderBodyWithChips = (text) => {
    if (!text) return '';
    
    const variableRegex = /\{\{(\w+)\}\}/gi;
    let result = text;
    let match;
    const replacements = [];
    
    while ((match = variableRegex.exec(text)) !== null) {
      const varName = match[1].toLowerCase();
      const value = variableValues[varName] || '';
      const chipClass = value ? 'has-value' : 'no-value';
      const chipHtml = `<span class="editor-chip ${chipClass}" contenteditable="false" data-variable="${varName}"><span class="chip-var">${match[1]}</span><span class="chip-separator">:</span><span class="chip-value">${value || '?'}</span></span>`;
      
      replacements.push({
        index: match.index,
        length: match[0].length,
        html: chipHtml
      });
    }
    
    // Replace from end to start to maintain indices
    replacements.reverse().forEach(repl => {
      result = result.substring(0, repl.index) + repl.html + result.substring(repl.index + repl.length);
    });
    
    // Convert newlines to <br> for display
    result = result.replace(/\n/g, '<br>');
    
    return result;
  };

  // Function to get final text with variables replaced
  const getFinalText = (text) => {
    if (!text) return '';
    
    let result = text;
    Object.entries(variableValues).forEach(([varName, value]) => {
      const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'gi');
      result = result.replace(regex, value);
    });
    
    return result;
  };

  const handleUseTemplate = (template) => {
    // Keep variables as-is, don't replace them automatically
    setSubject(template.subject);
    setBody(template.body);
    setShowTemplates(false);
    showToast?.(`Template "${template.name}" applied - Click variables below to replace them`, "info");
  };

  const handleSend = async () => {
    if (!to || !subject || !body) {
      showToast?.("Please fill in all required fields", "error");
      return;
    }

    setSending(true);
    try {
      // Replace variables with their values before sending
      const finalSubject = getFinalText(subject);
      const finalBody = getFinalText(body);
      
      await onSend({
        to,
        cc: cc || undefined,
        bcc: bcc || undefined,
        subject: finalSubject,
        body: finalBody.replace(/\n/g, "<br>"), // Convert newlines to HTML breaks
        threadId: email.threadId,
        messageId: email.id,
      });
      showToast?.("Email sent successfully", "success");
      onClose();
    } catch (error) {
      console.error("Error sending email:", error);
      showToast?.("Failed to send email", "error");
    } finally {
      setSending(false);
    }
  };

  if (!email) return null;

  return (
    <div className="email-reply-modal-overlay" onClick={onClose}>
      <div className="email-reply-modal" onClick={(e) => e.stopPropagation()}>
        <div className="email-reply-modal-header">
          <h3>{replyAll ? "Reply All" : "Reply"}</h3>
          <button className="close-button" onClick={onClose} title="Close">
            <FiX size={20} />
          </button>
        </div>

        <div className="email-reply-modal-body">
          <div className="email-field">
            <label>To: *</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              disabled={sending}
            />
          </div>

          {!showCcBcc && (
            <button 
              className="show-cc-bcc-button"
              onClick={() => setShowCcBcc(true)}
              type="button"
            >
              <FiChevronDown size={14} /> Cc / Bcc
            </button>
          )}

          {showCcBcc && (
            <>
              <div className="email-field">
                <label>
                  Cc:
                  <button 
                    className="hide-field-button"
                    onClick={() => {
                      setCc("");
                      if (!bcc) setShowCcBcc(false);
                    }}
                    type="button"
                    title="Clear and hide"
                  >
                    <FiChevronUp size={12} />
                  </button>
                </label>
                <input
                  type="email"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="cc@example.com (separate multiple with commas)"
                  disabled={sending}
                />
              </div>

              <div className="email-field">
                <label>
                  Bcc:
                  <button 
                    className="hide-field-button"
                    onClick={() => {
                      setBcc("");
                      if (!cc) setShowCcBcc(false);
                    }}
                    type="button"
                    title="Clear and hide"
                  >
                    <FiChevronUp size={12} />
                  </button>
                </label>
                <input
                  type="email"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  placeholder="bcc@example.com (separate multiple with commas)"
                  disabled={sending}
                />
              </div>
            </>
          )}

          <div className="email-field">
            <label>Subject: *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              disabled={sending}
            />
          </div>

          {templates.length > 0 && (
            <div className="template-selector">
              <button 
                className="template-toggle-button"
                onClick={() => setShowTemplates(!showTemplates)}
                type="button"
              >
                <FiFileText size={14} /> {showTemplates ? "Hide" : "Use"} Template
              </button>
              
              {showTemplates && (
                <div className="template-dropdown">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      className="template-option"
                      onClick={() => handleUseTemplate(template)}
                      type="button"
                    >
                      <strong>{template.name}</strong>
                      <span className="template-preview">{template.subject}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Variables Section */}
          {detectedVars.length > 0 && (
            <div className="variables-section">
              <div className="variables-header">
                <label>Template Variables</label>
                <span className="variables-count">{detectedVars.length} variable{detectedVars.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="variables-form">
                {detectedVars.map((varName, idx) => (
                  <div key={idx} className="variable-field">
                    <label>
                      <code>{`{{${varName}}}`}</code>
                    </label>
                    <input
                      type="text"
                      value={variableValues[varName] || ''}
                      onChange={(e) => setVariableValues(prev => ({ ...prev, [varName]: e.target.value }))}
                      placeholder={`Enter value for ${varName}...`}
                      disabled={sending}
                      className="variable-input"
                    />
                  </div>
                ))}
              </div>
              <p className="variables-hint">
                Edit values above - they'll be replaced when you send
              </p>
            </div>
          )}

          <div className="email-field">
            <label>Message: *</label>
            <div
              ref={(el) => {
                bodyEditorRef.current = el;
                // Initialize content only once on mount
                if (el && !el.hasAttribute('data-initialized')) {
                  el.innerHTML = renderBodyWithChips(body);
                  el.setAttribute('data-initialized', 'true');
                }
              }}
              className="body-editor"
              contentEditable={!sending}
              onInput={handleBodyEditorInput}
              suppressContentEditableWarning={true}
              data-placeholder="Type your message..."
            />
          </div>
        </div>

        <div className="email-reply-modal-footer">
          <button className="cancel-button" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button className="send-button" onClick={handleSend} disabled={sending}>
            <FiSend /> {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailReplyModal;

